<template>
  <div>
    <head>
      <link rel="shortcut icon" href="assets/images/favicon.svg">
      <link href="assets/css/bootstrap.min.css" id="bootstrap-style" rel="stylesheet" type="text/css" />
      <link href="assets/css/icons.min.css" rel="stylesheet" type="text/css" />
      <link href="assets/css/app.min.css" id="app-style" rel="stylesheet" type="text/css" />
      <link href="assets/css/custom.css" id="app-style" rel="stylesheet" type="text/css" />
    </head>

    <body @contextmenu.prevent data-sidebar="dark">
    <div id="layout-wrapper">
      <NavigationBar />

      <div id="content" class="content-minimize">
        <div class="page-content">
          <div class="container-fluid">

            <!-- Page Title -->
            <div class="row">
              <div class="col-12">
                <div class="page-title-box d-sm-flex align-items-center justify-content-between">
                  <h4 class="mb-sm-0 font-size-18">Visit Details</h4>
                  <div class="page-title-right">
                    <ol class="breadcrumb m-0">
                      <li class="breadcrumb-item"><a @click="goTo('hospital-dashboard')">Hospital</a></li>
                      <li class="breadcrumb-item"><a @click="goTo('queue-management')">Queue</a></li>
                      <li class="breadcrumb-item active">Visit Details</li>
                    </ol>
                  </div>
                </div>
              </div>
            </div>

            <!-- Loading -->
            <div v-if="loading" class="text-center py-5">
              <div class="spinner-border text-primary mb-3"></div>
              <p class="text-muted mb-0">Loading visit details...</p>
            </div>

            <div v-else>
              <!-- Visit Header Card -->
              <div class="card">
                <div class="card-body p-4">
                  <div class="row align-items-center">
                    <div class="col-lg-6">
                      <div class="d-flex align-items-center">
                        <div class="avatar-lg me-3">
                            <span class="avatar-title rounded-circle" :class="visit.gender === 'Male' ? 'bg-primary' : 'bg-pink'" style="font-size: 1.5rem;">
                              {{ getInitials(visit.patientName) }}
                            </span>
                        </div>
                        <div>
                          <h4 class="mb-1">{{ visit.patientName }}</h4>
                          <div class="d-flex flex-wrap gap-1">
                            <span class="badge bg-light text-dark">{{ visit.patientNo }}</span>
                            <span class="badge" :class="visit.gender === 'Male' ? 'bg-primary-subtle text-primary' : 'bg-pink-subtle text-pink'">{{ visit.gender }}</span>
                            <span class="badge bg-light text-dark">{{ visit.age }} years</span>
                          </div>
                        </div>
                      </div>
                    </div>
                    <div class="col-lg-6 mt-3 mt-lg-0">
                      <div class="d-flex justify-content-lg-end align-items-center gap-3">
                        <div class="text-end">
                          <p class="text-muted small mb-0">Visit No</p>
                          <h6 class="mb-0">{{ visit.visitNo }}</h6>
                        </div>
                        <div class="text-center">
                          <p class="text-muted small mb-0">Queue</p>
                          <span class="queue-badge-sm">{{ visit.queueNo }}</span>
                        </div>
                        <div class="text-end">
                          <p class="text-muted small mb-0">Status</p>
                          <span class="badge" :class="getStatusBadge(visit.visitStatus)">
                              {{ formatStatus(visit.visitStatus) }}
                            </span>
                        </div>
                      </div>
                    </div>
                  </div>

                  <!-- Alerts -->
                  <div v-if="visit.allergies || visit.chronicConditions" class="row mt-3">
                    <div class="col-md-6" v-if="visit.allergies">
                      <div class="alert alert-danger py-2 mb-0">
                        <small><i class="mdi mdi-alert-circle me-1"></i><strong>Allergies:</strong> {{ visit.allergies }}</small>
                      </div>
                    </div>
                    <div class="col-md-6" v-if="visit.chronicConditions">
                      <div class="alert alert-warning py-2 mb-0">
                        <small><i class="mdi mdi-heart-pulse me-1"></i><strong>Chronic:</strong> {{ visit.chronicConditions }}</small>
                      </div>
                    </div>
                  </div>

                  <!-- Quick Actions -->
                  <div class="d-flex flex-wrap gap-2 mt-3 pt-3 border-top">
                    <button class="btn btn-soft-primary btn-sm" @click="viewPatient">
                      <i class="mdi mdi-account me-1"></i> View Patient
                    </button>
                    <button v-if="visit.visitStatus === 'WAITING_TRIAGE'" class="btn btn-soft-warning btn-sm" @click="startTriage">
                      <i class="mdi mdi-pulse me-1"></i> Start Triage
                    </button>
                    <button v-if="visit.visitStatus === 'WAITING_CONSULTATION'" class="btn btn-soft-success btn-sm" @click="startConsultation">
                      <i class="mdi mdi-stethoscope me-1"></i> Start Consultation
                    </button>
                    <button class="btn btn-soft-info btn-sm" @click="goToBilling">
                      <i class="mdi mdi-cash-register me-1"></i> Billing
                    </button>
                    <button class="btn btn-soft-secondary btn-sm" @click="printVisit">
                      <i class="mdi mdi-printer me-1"></i> Print
                    </button>
                  </div>
                </div>
              </div>

              <div class="row">
                <!-- Left Column -->
                <div class="col-lg-4">
                  <!-- Visit Info Card -->
                  <div class="card">
                    <div class="card-body p-4">
                      <h6 class="mb-3"><i class="mdi mdi-clipboard-text me-2 text-primary"></i>Visit Information</h6>
                      <table class="table table-sm table-borderless mb-0">
                        <tbody>
                        <tr>
                          <td class="text-muted">Visit Type</td>
                          <td class="text-end">
                            <span class="badge" :class="getVisitTypeBadge(visit.visitType)">{{ visit.visitType }}</span>
                          </td>
                        </tr>
                        <tr>
                          <td class="text-muted">Category</td>
                          <td class="text-end">{{ visit.visitCategory || '-' }}</td>
                        </tr>
                        <tr>
                          <td class="text-muted">Payment</td>
                          <td class="text-end">{{ visit.paymentMode }}</td>
                        </tr>
                        <tr>
                          <td class="text-muted">Check-in</td>
                          <td class="text-end">{{ formatDateTime(visit.dateCreated) }}</td>
                        </tr>
                        <tr v-if="visit.assignedDoctorName">
                          <td class="text-muted">Doctor</td>
                          <td class="text-end">Dr. {{ visit.assignedDoctorName }}</td>
                        </tr>
                        </tbody>
                      </table>
                    </div>
                  </div>

                  <!-- Chief Complaint -->
                  <div class="card">
                    <div class="card-body p-4">
                      <h6 class="mb-3"><i class="mdi mdi-message-text me-2 text-primary"></i>Chief Complaint</h6>
                      <p class="mb-0 text-muted">{{ visit.chiefComplaint || 'Not recorded' }}</p>
                    </div>
                  </div>

                  <!-- Timeline -->
                  <div class="card">
                    <div class="card-body p-4">
                      <h6 class="mb-3"><i class="mdi mdi-timeline me-2 text-primary"></i>Visit Timeline</h6>
                      <div class="timeline-wrapper">
                        <div class="timeline-item" :class="{ 'completed': true }">
                          <div class="timeline-marker bg-success"></div>
                          <div class="timeline-content">
                            <h6 class="mb-0">Registration</h6>
                            <small class="text-muted">{{ formatDateTime(visit.dateCreated) }}</small>
                          </div>
                        </div>
                        <div class="timeline-item" :class="{ 'completed': triage }">
                          <div class="timeline-marker" :class="triage ? 'bg-success' : 'bg-secondary'"></div>
                          <div class="timeline-content">
                            <h6 class="mb-0">Triage</h6>
                            <small class="text-muted">{{ triage ? formatDateTime(triage.dateCreated) : 'Pending' }}</small>
                          </div>
                        </div>
                        <div class="timeline-item" :class="{ 'completed': consultation }">
                          <div class="timeline-marker" :class="consultation ? 'bg-success' : 'bg-secondary'"></div>
                          <div class="timeline-content">
                            <h6 class="mb-0">Consultation</h6>
                            <small class="text-muted">{{ consultation ? formatDateTime(consultation.dateCreated) : 'Pending' }}</small>
                          </div>
                        </div>
                        <div class="timeline-item" :class="{ 'completed': visit.visitStatus === 'COMPLETED' }">
                          <div class="timeline-marker" :class="visit.visitStatus === 'COMPLETED' ? 'bg-success' : 'bg-secondary'"></div>
                          <div class="timeline-content">
                            <h6 class="mb-0">Completed</h6>
                            <small class="text-muted">{{ visit.dateCompleted ? formatDateTime(visit.dateCompleted) : 'Pending' }}</small>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                <!-- Right Column: Tabs -->
                <div class="col-lg-8">
                  <div class="card">
                    <div class="card-body p-4">
                      <!-- Tabs -->
                      <ul class="nav nav-tabs nav-tabs-custom mb-4">
                        <li class="nav-item">
                          <a class="nav-link" :class="{ active: activeTab === 'triage' }" @click="activeTab = 'triage'" href="javascript:void(0);">
                            <i class="mdi mdi-pulse me-1"></i> Triage
                          </a>
                        </li>
                        <li class="nav-item">
                          <a class="nav-link" :class="{ active: activeTab === 'consultation' }" @click="activeTab = 'consultation'" href="javascript:void(0);">
                            <i class="mdi mdi-stethoscope me-1"></i> Consultation
                          </a>
                        </li>
                        <li class="nav-item">
                          <a class="nav-link" :class="{ active: activeTab === 'lab' }" @click="activeTab = 'lab'" href="javascript:void(0);">
                            <i class="mdi mdi-test-tube me-1"></i> Lab
                          </a>
                        </li>
                        <li class="nav-item">
                          <a class="nav-link" :class="{ active: activeTab === 'pharmacy' }" @click="activeTab = 'pharmacy'" href="javascript:void(0);">
                            <i class="mdi mdi-pill me-1"></i> Pharmacy
                          </a>
                        </li>
                        <li class="nav-item">
                          <a class="nav-link" :class="{ active: activeTab === 'charges' }" @click="activeTab = 'charges'" href="javascript:void(0);">
                            <i class="mdi mdi-currency-usd me-1"></i> Charges
                          </a>
                        </li>
                      </ul>

                      <!-- Triage Tab -->
                      <div v-if="activeTab === 'triage'">
                        <div v-if="triage">
                          <h6 class="text-uppercase text-muted small mb-3">Vital Signs</h6>
                          <div class="row g-3 mb-4">
                            <div class="col-4 col-md-2">
                              <div class="vital-card">
                                <i class="mdi mdi-thermometer text-danger"></i>
                                <h5 class="mb-0">{{ triage.temperature }}°C</h5>
                                <small class="text-muted">Temp</small>
                              </div>
                            </div>
                            <div class="col-4 col-md-2">
                              <div class="vital-card">
                                <i class="mdi mdi-heart-pulse text-danger"></i>
                                <h5 class="mb-0">{{ triage.bloodPressureSystolic }}/{{ triage.bloodPressureDiastolic }}</h5>
                                <small class="text-muted">BP</small>
                              </div>
                            </div>
                            <div class="col-4 col-md-2">
                              <div class="vital-card">
                                <i class="mdi mdi-pulse text-primary"></i>
                                <h5 class="mb-0">{{ triage.pulseRate }}</h5>
                                <small class="text-muted">Pulse</small>
                              </div>
                            </div>
                            <div class="col-4 col-md-2">
                              <div class="vital-card">
                                <i class="mdi mdi-lungs text-info"></i>
                                <h5 class="mb-0">{{ triage.respiratoryRate }}</h5>
                                <small class="text-muted">Resp</small>
                              </div>
                            </div>
                            <div class="col-4 col-md-2">
                              <div class="vital-card">
                                <i class="mdi mdi-percent text-success"></i>
                                <h5 class="mb-0">{{ triage.oxygenSaturation }}%</h5>
                                <small class="text-muted">SpO2</small>
                              </div>
                            </div>
                            <div class="col-4 col-md-2">
                              <div class="vital-card">
                                <i class="mdi mdi-scale text-warning"></i>
                                <h5 class="mb-0">{{ triage.weight }}kg</h5>
                                <small class="text-muted">Weight</small>
                              </div>
                            </div>
                          </div>

                          <div class="mb-3">
                              <span class="badge" :class="getTriageBadge(triage.triageCategory)">
                                Triage: {{ formatTriageCategory(triage.triageCategory) }}
                              </span>
                          </div>

                          <div v-if="triage.chiefComplaint" class="mb-3">
                            <h6 class="text-uppercase text-muted small">Chief Complaint</h6>
                            <p class="mb-0">{{ triage.chiefComplaint }}</p>
                          </div>

                          <div v-if="triage.historyOfPresentIllness" class="mb-3">
                            <h6 class="text-uppercase text-muted small">History of Present Illness</h6>
                            <p class="mb-0">{{ triage.historyOfPresentIllness }}</p>
                          </div>

                          <div class="text-muted small mt-4 pt-3 border-top">
                            Triaged by: {{ triage.triageBy }} on {{ formatDateTime(triage.dateCreated) }}
                          </div>
                        </div>
                        <div v-else class="text-center py-5">
                          <div class="empty-state-icon mb-3">
                            <i class="mdi mdi-pulse"></i>
                          </div>
                          <p class="text-muted mb-3">Triage not yet completed</p>
                          <button v-if="visit.visitStatus === 'WAITING_TRIAGE'" class="btn btn-outline-primary btn-sm" @click="startTriage">
                            <i class="mdi mdi-plus me-1"></i> Start Triage
                          </button>
                        </div>
                      </div>

                      <!-- Consultation Tab -->
                      <div v-if="activeTab === 'consultation'">
                        <div v-if="consultation">
                          <div class="mb-3">
                            <h6 class="text-uppercase text-muted small">Chief Complaint</h6>
                            <p class="mb-0">{{ consultation.chiefComplaint || '-' }}</p>
                          </div>
                          <div v-if="consultation.historyOfPresentIllness" class="mb-3">
                            <h6 class="text-uppercase text-muted small">History of Present Illness</h6>
                            <p class="mb-0">{{ consultation.historyOfPresentIllness }}</p>
                          </div>
                          <div v-if="consultation.physicalExamination" class="mb-3">
                            <h6 class="text-uppercase text-muted small">Physical Examination</h6>
                            <p class="mb-0">{{ consultation.physicalExamination }}</p>
                          </div>
                          <div class="mb-3">
                            <h6 class="text-uppercase text-muted small">Provisional Diagnosis</h6>
                            <p class="mb-0">
                              {{ consultation.provisionalDiagnosis || '-' }}
                              <span v-if="consultation.icd10Codes" class="badge bg-light text-dark ms-1">{{ formatICD(consultation.icd10Codes) }}</span>
                            </p>
                          </div>
                          <div v-if="consultation.finalDiagnosis" class="mb-3">
                            <h6 class="text-uppercase text-muted small">Final Diagnosis</h6>
                            <p class="mb-0">{{ consultation.finalDiagnosis }}</p>
                          </div>
                          <div v-if="consultation.treatmentPlan" class="mb-3">
                            <h6 class="text-uppercase text-muted small">Treatment Plan</h6>
                            <p class="mb-0">{{ consultation.treatmentPlan }}</p>
                          </div>
                          <div v-if="consultation.doctorNotes" class="mb-3">
                            <h6 class="text-uppercase text-muted small">Doctor Notes</h6>
                            <p class="mb-0">{{ consultation.doctorNotes }}</p>
                          </div>
                          <div class="text-muted small mt-4 pt-3 border-top">
                            Consultation by: Dr. {{ consultation.doctorName || consultation.createdBy }} on {{ formatDateTime(consultation.dateCreated) }}
                          </div>
                        </div>
                        <div v-else class="text-center py-5">
                          <div class="empty-state-icon mb-3">
                            <i class="mdi mdi-stethoscope"></i>
                          </div>
                          <p class="text-muted">Consultation not yet completed</p>
                        </div>
                      </div>

                      <!-- Lab Tab -->
                      <div v-if="activeTab === 'lab'">
                        <div v-if="labRequests.length > 0">
                          <div class="table-responsive">
                            <table class="table table-hover align-middle mb-0">
                              <thead class="table-light">
                              <tr>
                                <th>Test</th>
                                <th>Result</th>
                                <th>Reference</th>
                                <th>Flag</th>
                                <th>Status</th>
                                <th>Actions</th>
                              </tr>
                              </thead>
                              <tbody>
                              <tr v-for="lab in labRequests" :key="lab.requestID">
                                <td>
                                  <span class="fw-medium">{{ lab.testName }}</span>
                                  <small class="text-muted d-block">{{ lab.sampleType }}</small>
                                </td>
                                <td>
                                      <span v-if="lab.result" class="fw-bold" :class="getLabFlagClass(lab.flag)">
                                        {{ lab.result }} {{ lab.unit }}
                                      </span>
                                  <span v-else class="text-muted">Pending</span>
                                </td>
                                <td>
                                  <small>{{ lab.normalRange || '-' }}</small>
                                </td>
                                <td>
                                  <span v-if="lab.flag" class="badge" :class="getLabFlagBadge(lab.flag)">{{ lab.flag }}</span>
                                  <span v-else-if="lab.result" class="text-success"><i class="mdi mdi-check-circle"></i></span>
                                  <span v-else>-</span>
                                </td>
                                <td>
                                  <span class="badge" :class="getLabStatusBadge(lab.requestStatus)">{{ lab.requestStatus }}</span>
                                  <small v-if="lab.resultStatus === 'Verified'" class="text-success d-block">
                                    <i class="mdi mdi-check-decagram"></i> Verified
                                  </small>
                                </td>
                                <td>
                                  <button v-if="lab.requestStatus === 'Completed'"
                                          class="btn btn-sm btn-soft-success"
                                          @click="viewLabReport(lab.requestID)"
                                          title="View Full Report">
                                    <i class="mdi mdi-file-document-outline me-1"></i> Report
                                  </button>
                                  <span v-else class="text-muted small">-</span>
                                </td>
                              </tr>
                              </tbody>
                            </table>
                          </div>
                        </div>
                        <div v-else class="text-center py-5">
                          <div class="empty-state-icon mb-3">
                            <i class="mdi mdi-test-tube"></i>
                          </div>
                          <p class="text-muted">No lab requests for this visit</p>
                        </div>
                      </div>

                      <!-- Pharmacy Tab -->
                      <div v-if="activeTab === 'pharmacy'">
                        <div v-if="prescriptions.length > 0">
                          <div v-for="rx in prescriptions" :key="rx.prescriptionID" class="border rounded p-3 mb-2">
                            <div class="d-flex justify-content-between align-items-center">
                              <div>
                                <h6 class="mb-1">{{ rx.prescriptionNo }}</h6>
                                <p class="mb-0 small text-muted">{{ rx.diagnosis }}</p>
                                <small class="text-muted">{{ formatDateTime(rx.dateCreated) }}</small>
                              </div>
                              <span class="badge" :class="getRxStatusBadge(rx.prescriptionStatus)">{{ rx.prescriptionStatus }}</span>
                            </div>
                          </div>
                        </div>
                        <div v-else class="text-center py-5">
                          <div class="empty-state-icon mb-3">
                            <i class="mdi mdi-pill"></i>
                          </div>
                          <p class="text-muted">No prescriptions for this visit</p>
                        </div>
                      </div>

                      <!-- Charges Tab -->
                      <div v-if="activeTab === 'charges'">
                        <!-- Loading -->
                        <div v-if="loadingBilling" class="text-center py-4">
                          <div class="spinner-border spinner-border-sm text-primary"></div>
                          <p class="text-muted small mt-2 mb-0">Loading billing data...</p>
                        </div>

                        <div v-else>
                          <!-- Summary Cards -->
                          <div class="row g-3 mb-4">
                            <div class="col-6 col-md-3">
                              <div class="card bg-primary text-white mb-0">
                                <div class="card-body py-2 text-center">
                                  <small class="d-block opacity-75">Total</small>
                                  <h5 class="mb-0 text-white">KES {{ formatNumber(billingSummary.grandTotal) }}</h5>
                                </div>
                              </div>
                            </div>
                            <div class="col-6 col-md-3">
                              <div class="card bg-success text-white mb-0">
                                <div class="card-body py-2 text-center">
                                  <small class="d-block opacity-75">Billed</small>
                                  <h5 class="mb-0 text-white">KES {{ formatNumber(billingSummary.billedTotal) }}</h5>
                                </div>
                              </div>
                            </div>
                            <div class="col-6 col-md-3">
                              <div class="card bg-warning text-dark mb-0">
                                <div class="card-body py-2 text-center">
                                  <small class="d-block opacity-75">Unbilled</small>
                                  <h5 class="mb-0 text-white">KES {{ formatNumber(billingSummary.unbilledTotal) }}</h5>
                                </div>
                              </div>
                            </div>
                            <div class="col-6 col-md-3">
                              <div class="card bg-info text-white mb-0">
                                <div class="card-body py-2 text-center">
                                  <small class="d-block opacity-75">Items</small>
                                  <h5 class="mb-0 text-white">{{ billingSummary.totalItems }}</h5>
                                </div>
                              </div>
                            </div>
                          </div>

                          <!-- Generated Invoices -->
                          <div v-if="generatedBills.length > 0" class="mb-4">
                            <h6 class="text-muted mb-3"><i class="mdi mdi-file-document-multiple me-1"></i>Generated Invoices</h6>
                            <div class="list-group">
                              <div v-for="bill in generatedBills" :key="bill.billNo" class="list-group-item d-flex justify-content-between align-items-center">
                                <div>
                                  <strong class="text-success">{{ bill.billNo }}</strong>
                                  <small class="text-muted d-block">{{ formatDateTime(bill.billedAt) }} by {{ bill.billedBy }}</small>
                                </div>
                                <div>
                                  <button class="btn btn-sm btn-outline-primary me-1" @click="viewBill(bill.billNo)" title="View">
                                    <i class="mdi mdi-eye"></i>
                                  </button>
                                  <button class="btn btn-sm btn-outline-success" @click="printBill(bill.billNo)" title="Print">
                                    <i class="mdi mdi-printer"></i>
                                  </button>
                                </div>
                              </div>
                            </div>
                          </div>

                          <!-- Charges by Category -->
                          <div v-if="hasCharges">
                            <h6 class="text-muted mb-3"><i class="mdi mdi-format-list-bulleted me-1"></i>Charges by Category</h6>

                            <!-- Consultation -->
                            <div v-if="billingCategories.CONSULTATION?.items?.length > 0" class="mb-3">
                              <div class="d-flex justify-content-between align-items-center bg-light px-3 py-2 rounded-top">
                                <strong><i class="mdi mdi-stethoscope me-2 text-primary"></i>Consultation</strong>
                                <span>KES {{ formatNumber(billingCategories.CONSULTATION.total) }}</span>
                              </div>
                              <table class="table table-sm table-bordered mb-0">
                                <tbody>
                                <tr v-for="item in billingCategories.CONSULTATION.items" :key="item.visitChargeID">
                                  <td>{{ item.serviceName }}</td>
                                  <td class="text-center" style="width: 60px;">x{{ item.quantity }}</td>
                                  <td class="text-end" style="width: 120px;">KES {{ formatNumber(item.netAmount) }}</td>
                                  <td class="text-center" style="width: 80px;">
                                      <span class="badge" :class="item.billed === 'Yes' ? 'bg-success' : 'bg-warning'">
                                        {{ item.billed === 'Yes' ? 'Billed' : 'Pending' }}
                                      </span>
                                  </td>
                                </tr>
                                </tbody>
                              </table>
                            </div>

                            <!-- Lab -->
                            <div v-if="billingCategories.LAB?.items?.length > 0" class="mb-3">
                              <div class="d-flex justify-content-between align-items-center bg-light px-3 py-2 rounded-top">
                                <strong><i class="mdi mdi-test-tube me-2 text-info"></i>Laboratory</strong>
                                <span>KES {{ formatNumber(billingCategories.LAB.total) }}</span>
                              </div>
                              <table class="table table-sm table-bordered mb-0">
                                <tbody>
                                <tr v-for="item in billingCategories.LAB.items" :key="item.visitChargeID">
                                  <td>{{ item.serviceName }}</td>
                                  <td class="text-center" style="width: 60px;">x{{ item.quantity }}</td>
                                  <td class="text-end" style="width: 120px;">KES {{ formatNumber(item.netAmount) }}</td>
                                  <td class="text-center" style="width: 80px;">
                                      <span class="badge" :class="item.billed === 'Yes' ? 'bg-success' : 'bg-warning'">
                                        {{ item.billed === 'Yes' ? 'Billed' : 'Pending' }}
                                      </span>
                                  </td>
                                </tr>
                                </tbody>
                              </table>
                            </div>

                            <!-- Pharmacy -->
                            <div v-if="billingCategories.PHARMACY?.items?.length > 0" class="mb-3">
                              <div class="d-flex justify-content-between align-items-center bg-light px-3 py-2 rounded-top">
                                <strong><i class="mdi mdi-pill me-2 text-success"></i>Pharmacy</strong>
                                <span>KES {{ formatNumber(billingCategories.PHARMACY.total) }}</span>
                              </div>
                              <table class="table table-sm table-bordered mb-0">
                                <tbody>
                                <tr v-for="item in billingCategories.PHARMACY.items" :key="item.visitChargeID">
                                  <td>{{ item.serviceName }}</td>
                                  <td class="text-center" style="width: 60px;">x{{ item.quantity }}</td>
                                  <td class="text-end" style="width: 120px;">KES {{ formatNumber(item.netAmount) }}</td>
                                  <td class="text-center" style="width: 80px;">
                                      <span class="badge" :class="item.billed === 'Yes' ? 'bg-success' : 'bg-warning'">
                                        {{ item.billed === 'Yes' ? 'Billed' : 'Pending' }}
                                      </span>
                                  </td>
                                </tr>
                                </tbody>
                              </table>
                            </div>

                            <!-- Radiology -->
                            <div v-if="billingCategories.RADIOLOGY?.items?.length > 0" class="mb-3">
                              <div class="d-flex justify-content-between align-items-center bg-light px-3 py-2 rounded-top">
                                <strong><i class="mdi mdi-radiology-box me-2 text-purple"></i>Radiology</strong>
                                <span>KES {{ formatNumber(billingCategories.RADIOLOGY.total) }}</span>
                              </div>
                              <table class="table table-sm table-bordered mb-0">
                                <tbody>
                                <tr v-for="item in billingCategories.RADIOLOGY.items" :key="item.visitChargeID">
                                  <td>{{ item.serviceName }}</td>
                                  <td class="text-center" style="width: 60px;">x{{ item.quantity }}</td>
                                  <td class="text-end" style="width: 120px;">KES {{ formatNumber(item.netAmount) }}</td>
                                  <td class="text-center" style="width: 80px;">
                                      <span class="badge" :class="item.billed === 'Yes' ? 'bg-success' : 'bg-warning'">
                                        {{ item.billed === 'Yes' ? 'Billed' : 'Pending' }}
                                      </span>
                                  </td>
                                </tr>
                                </tbody>
                              </table>
                            </div>

                            <!-- Procedure -->
                            <div v-if="billingCategories.PROCEDURE?.items?.length > 0" class="mb-3">
                              <div class="d-flex justify-content-between align-items-center bg-light px-3 py-2 rounded-top">
                                <strong><i class="mdi mdi-medical-bag me-2 text-danger"></i>Procedures</strong>
                                <span>KES {{ formatNumber(billingCategories.PROCEDURE.total) }}</span>
                              </div>
                              <table class="table table-sm table-bordered mb-0">
                                <tbody>
                                <tr v-for="item in billingCategories.PROCEDURE.items" :key="item.visitChargeID">
                                  <td>{{ item.serviceName }}</td>
                                  <td class="text-center" style="width: 60px;">x{{ item.quantity }}</td>
                                  <td class="text-end" style="width: 120px;">KES {{ formatNumber(item.netAmount) }}</td>
                                  <td class="text-center" style="width: 80px;">
                                      <span class="badge" :class="item.billed === 'Yes' ? 'bg-success' : 'bg-warning'">
                                        {{ item.billed === 'Yes' ? 'Billed' : 'Pending' }}
                                      </span>
                                  </td>
                                </tr>
                                </tbody>
                              </table>
                            </div>

                            <!-- Other -->
                            <div v-if="billingCategories.OTHER?.items?.length > 0" class="mb-3">
                              <div class="d-flex justify-content-between align-items-center bg-light px-3 py-2 rounded-top">
                                <strong><i class="mdi mdi-plus-circle me-2 text-secondary"></i>Other</strong>
                                <span>KES {{ formatNumber(billingCategories.OTHER.total) }}</span>
                              </div>
                              <table class="table table-sm table-bordered mb-0">
                                <tbody>
                                <tr v-for="item in billingCategories.OTHER.items" :key="item.visitChargeID">
                                  <td>{{ item.serviceName }}</td>
                                  <td class="text-center" style="width: 60px;">x{{ item.quantity }}</td>
                                  <td class="text-end" style="width: 120px;">KES {{ formatNumber(item.netAmount) }}</td>
                                  <td class="text-center" style="width: 80px;">
                                      <span class="badge" :class="item.billed === 'Yes' ? 'bg-success' : 'bg-warning'">
                                        {{ item.billed === 'Yes' ? 'Billed' : 'Pending' }}
                                      </span>
                                  </td>
                                </tr>
                                </tbody>
                              </table>
                            </div>
                          </div>

                          <!-- No Charges -->
                          <div v-if="!hasCharges" class="text-center py-5">
                            <div class="empty-state-icon mb-3">
                              <i class="mdi mdi-currency-usd"></i>
                            </div>
                            <p class="text-muted">No charges recorded for this visit</p>
                          </div>

                          <!-- Actions -->
                          <div v-if="hasCharges" class="mt-4 pt-3 border-top d-flex justify-content-end gap-2">
                            <button v-if="generatedBills.length > 0" class="btn btn-success btn-sm" @click="printBill(generatedBills[0].billNo)">
                              <i class="mdi mdi-printer me-1"></i> Print Latest Invoice
                            </button>
                            <button class="btn btn-outline-primary btn-sm" @click="loadBillingSummary">
                              <i class="mdi mdi-refresh me-1"></i> Refresh
                            </button>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

          </div>
        </div>

        <footer class="footer">
          <div class="container-fluid">
            <div class="row">
              <div class="col-sm-6"></div>
              <div class="col-sm-6">
                <div class="text-sm-end d-sm-block">County Hospital Management System</div>
              </div>
            </div>
          </div>
        </footer>
      </div>
    </div>
    <!-- Toast Notification -->
    <ToastNotification ref="toast" />
    </body>
  </div>
</template>

<script>
/* eslint-disable */
import NavigationBar from "@/components/Navigation.vue";
import ToastNotification from "@/components/hospital/ToastNotification.vue";
import { execute, hospital } from "@/api";

export default {
  name: "VisitDetails",
  components: { NavigationBar, ToastNotification },
  props: {
    visitID: {
      type: String,
      default: ''
    }
  },
  data() {
    return {
      loading: true,
      currentVisitID: '',
      visit: {},
      triage: null,
      consultation: null,
      labRequests: [],
      prescriptions: [],
      charges: [],
      chargeTotals: {},
      activeTab: 'triage',

      // Billing
      loadingBilling: false,
      billingCategories: {},
      billingSummary: {
        grandTotal: 0,
        billedTotal: 0,
        unbilledTotal: 0,
        totalItems: 0
      },
      generatedBills: [],

      facilityID: '',
      sessionNames: '',
      sessionIdNo: ''
    };
  },
  computed: {
    hasCharges() {
      const cats = this.billingCategories || {};
      return Object.values(cats).some(cat => cat.items && cat.items.length > 0);
    }
  },
  methods: {
    goTo(route) {
      this.$router.push('/' + route);
    },

    getInitials(name) {
      if (!name) return '?';
      return name.split(' ').map(n => n[0]).join('').substring(0, 2).toUpperCase();
    },

    formatDate(date) {
      if (!date) return '-';
      return new Date(date).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
    },

    formatDateTime(date) {
      if (!date) return '-';
      return new Date(date).toLocaleString('en-GB', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' });
    },

    formatCurrency(amount) {
      return 'KES ' + Number(amount || 0).toLocaleString();
    },

    formatNumber(num) {
      return Number(num || 0).toLocaleString();
    },

    getVisitTypeBadge(type) {
      const badges = { 'OPD': 'bg-primary', 'EMERGENCY': 'bg-danger', 'REFERRAL': 'bg-info', 'FOLLOW_UP': 'bg-secondary' };
      return badges[type] || 'bg-secondary';
    },

    getStatusBadge(status) {
      const badges = {
        'WAITING_TRIAGE': 'bg-warning text-dark', 'IN_TRIAGE': 'bg-warning',
        'WAITING_CONSULTATION': 'bg-primary', 'IN_CONSULTATION': 'bg-info',
        'WAITING_LAB': 'bg-info', 'WAITING_PHARMACY': 'bg-success',
        'COMPLETED': 'bg-success', 'CANCELLED': 'bg-danger'
      };
      return badges[status] || 'bg-secondary';
    },

    formatStatus(status) {
      if (!status) return '-';
      return status.replace(/_/g, ' ').replace(/WAITING/g, 'Waiting').replace(/^IN /g, 'In ');
    },

    getTriageBadge(category) {
      const badges = { 'EMERGENCY': 'bg-danger', 'URGENT': 'bg-warning', 'LESS_URGENT': 'bg-info', 'NON_URGENT': 'bg-success' };
      return badges[category] || 'bg-secondary';
    },

    formatTriageCategory(category) {
      const labels = { 'EMERGENCY': 'Emergency', 'URGENT': 'Urgent', 'LESS_URGENT': 'Less Urgent', 'NON_URGENT': 'Non-Urgent' };
      return labels[category] || category;
    },

    getLabStatusBadge(status) {
      const badges = {
        'Pending': 'bg-warning text-dark',
        'Sample Collected': 'bg-info',
        'Processing': 'bg-primary',
        'Completed': 'bg-success',
        'PENDING': 'bg-warning',
        'SAMPLE_COLLECTED': 'bg-info',
        'PROCESSING': 'bg-primary',
        'COMPLETED': 'bg-success'
      };
      return badges[status] || 'bg-secondary';
    },

    getLabFlagClass(flag) {
      if (!flag) return '';
      if (flag.includes('Critical')) return 'text-danger';
      if (flag === 'High' || flag === 'Low') return 'text-warning';
      return '';
    },

    getLabFlagBadge(flag) {
      if (!flag) return 'bg-success';
      if (flag.includes('Critical')) return 'bg-danger';
      if (flag === 'High' || flag === 'Low') return 'bg-warning text-dark';
      return 'bg-info';
    },

    viewLabReport(requestID) {
      this.$router.push('/lab-report/' + requestID);
    },

    formatICD(icdCodes) {
      if (!icdCodes) return '';
      try {
        const codes = JSON.parse(icdCodes);
        if (Array.isArray(codes)) {
          return codes.map(c => c.code || c).join(', ');
        }
        return icdCodes;
      } catch (e) {
        return icdCodes;
      }
    },

    getRxStatusBadge(status) {
      const badges = { 'PENDING': 'bg-warning', 'PARTIAL': 'bg-info', 'DISPENSED': 'bg-success' };
      return badges[status] || 'bg-secondary';
    },

    loadVisit() {
      this.loading = true;
      const data = new FormData();
      data.append("function", "getVisit");
      data.append("visitID", this.currentVisitID);

      execute(data, hospital)
          .then((res) => {
            this.loading = false;
            if (res.data.success) {
              this.visit = res.data.data.visit;
              this.loadTriage();
              this.loadConsultation();
              this.loadLabRequests();
              this.loadCharges();
            } else {
              this.$refs.toast.error('Visit not found');
              this.goTo('queue-management');
            }
          })
          .catch((e) => {
            this.loading = false;
            console.error(e);
          });
    },

    loadTriage() {
      const data = new FormData();
      data.append("function", "getTriage");
      data.append("visitID", this.currentVisitID);

      execute(data, hospital)
          .then((res) => {
            if (res.data.success) {
              this.triage = res.data.data.triage;
            }
          })
          .catch((e) => console.error(e));
    },

    loadConsultation() {
      const data = new FormData();
      data.append("function", "getConsultation");
      data.append("visitID", this.currentVisitID);

      execute(data, hospital)
          .then((res) => {
            if (res.data.success) {
              this.consultation = res.data.data.consultation;
            }
          })
          .catch((e) => console.error(e));
    },

    loadLabRequests() {
      const data = new FormData();
      data.append("function", "getLabResultsByVisit");
      data.append("visitID", this.currentVisitID);

      execute(data, hospital)
          .then((res) => {
            if (res.data.success) {
              this.labRequests = res.data.data.labResults || [];
            }
          })
          .catch((e) => console.error(e));
    },

    loadCharges() {
      // Keep for backward compatibility - calls new method
      this.loadBillingSummary();
    },

    loadBillingSummary() {
      this.loadingBilling = true;

      const data = new FormData();
      data.append("function", "getVisitBillingSummary");
      data.append("visitID", this.currentVisitID);
      data.append("sessionNames", this.sessionNames);

      execute(data, hospital)
          .then((res) => {
            this.loadingBilling = false;
            if (res.data.success) {
              const responseData = res.data.data || {};
              this.billingCategories = responseData.categories || {};
              this.billingSummary = responseData.summary || {
                grandTotal: 0,
                billedTotal: 0,
                unbilledTotal: 0,
                totalItems: 0
              };
              this.generatedBills = responseData.bills || [];

              // Also populate old properties for backward compatibility
              this.charges = [];
              Object.values(this.billingCategories).forEach(cat => {
                if (cat.items) {
                  this.charges = this.charges.concat(cat.items);
                }
              });
              this.chargeTotals = { grossTotal: this.billingSummary.grandTotal };
            }
          })
          .catch((e) => {
            this.loadingBilling = false;
            console.error(e);
          });
    },

    viewBill(billNo) {
      if (!billNo) return;
      localStorage['params'] = JSON.stringify({ billNo: billNo });
      const routeData = this.$router.resolve({ name: 'bill' });
      window.open(routeData.href, '_blank');
    },

    printBill(billNo) {
      if (!billNo) return;
      localStorage['params'] = JSON.stringify({ billNo: billNo });
      const routeData = this.$router.resolve({ name: 'bill' });
      window.open(routeData.href, '_blank');
    },

    viewPatient() {
      this.$router.push('/patient-profile/' + this.visit.patientID);
    },

    startTriage() {
      this.$router.push('/triage-assessment/' + this.currentVisitID);
    },

    startConsultation() {
      this.$router.push('/consultation/' + this.currentVisitID);
    },

    goToBilling() {
      this.$router.push('/billing/' + this.currentVisitID);
    },

    printVisit() {
      window.print();
    }
  },
  mounted() {
    this.currentVisitID = this.visitID || this.$route.params.id;
    this.facilityID = sessionStorage.getItem("hospitalFacilityID") || '';
    this.sessionNames = sessionStorage.getItem("sessionNames") || '';
    this.sessionIdNo = sessionStorage.getItem("sessionIdNo") || '';

    if (this.currentVisitID) {
      this.loadVisit();
    } else {
      this.$refs.toast.error('No visit specified');
      this.goTo('queue-management');
    }
  }
};
</script>

<style scoped>
/* Avatar */
.avatar-lg { width: 5rem; height: 5rem; }
.avatar-title {
  display: flex; align-items: center; justify-content: center;
  width: 100%; height: 100%; font-weight: 600; color: #fff;
}

/* Colors */
.bg-primary-subtle { background-color: rgba(85, 110, 230, 0.15); }
.bg-pink-subtle { background-color: rgba(232, 62, 140, 0.15); }
.bg-pink { background-color: #e83e8c !important; }
.text-pink { color: #e83e8c !important; }
.text-purple { color: #6f42c1 !important; }

/* Queue Badge */
.queue-badge-sm {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 40px;
  background: linear-gradient(135deg, #556ee6 0%, #3b5de7 100%);
  color: #fff;
  font-weight: 700;
  border-radius: 8px;
}

/* Soft Buttons */
.btn-soft-primary { background-color: rgba(85, 110, 230, 0.1); color: #556ee6; border: none; }
.btn-soft-primary:hover { background-color: #556ee6; color: #fff; }

.btn-soft-warning { background-color: rgba(241, 180, 76, 0.1); color: #f1b44c; border: none; }
.btn-soft-warning:hover { background-color: #f1b44c; color: #fff; }

.btn-soft-success { background-color: rgba(52, 195, 143, 0.1); color: #34c38f; border: none; }
.btn-soft-success:hover { background-color: #34c38f; color: #fff; }

.btn-soft-secondary { background-color: rgba(116, 120, 141, 0.1); color: #74788d; border: none; }
.btn-soft-secondary:hover { background-color: #74788d; color: #fff; }

.btn-soft-info { background-color: rgba(23, 162, 184, 0.1); color: #17a2b8; border: none; }
.btn-soft-info:hover { background-color: #17a2b8; color: #fff; }

/* Nav Tabs */
.nav-tabs-custom .nav-link {
  color: #495057;
  border: none;
  padding: 0.75rem 1rem;
  font-weight: 500;
}

.nav-tabs-custom .nav-link.active {
  color: #556ee6;
  border-bottom: 2px solid #556ee6;
  background: transparent;
}

.nav-tabs-custom .nav-link:hover:not(.active) {
  color: #556ee6;
}

/* Vital Cards */
.vital-card {
  text-align: center;
  padding: 1rem;
  background: #f8f9fa;
  border-radius: 8px;
}

.vital-card i {
  font-size: 1.5rem;
  margin-bottom: 0.5rem;
}

.vital-card h5 {
  font-size: 1rem;
  margin-bottom: 0.25rem;
}

/* Timeline */
.timeline-wrapper {
  position: relative;
  padding-left: 30px;
}

.timeline-item {
  position: relative;
  padding-bottom: 20px;
}

.timeline-item:last-child {
  padding-bottom: 0;
}

.timeline-item::before {
  content: '';
  position: absolute;
  left: -23px;
  top: 10px;
  bottom: -10px;
  width: 2px;
  background: #e9ecef;
}

.timeline-item:last-child::before {
  display: none;
}

.timeline-marker {
  position: absolute;
  left: -30px;
  top: 0;
  width: 14px;
  height: 14px;
  border-radius: 50%;
}

.timeline-content h6 {
  font-size: 0.9rem;
  margin-bottom: 2px;
}

/* Empty State */
.empty-state-icon {
  width: 60px;
  height: 60px;
  background: #f8f9fa;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 auto;
}

.empty-state-icon i {
  font-size: 28px;
  color: #adb5bd;
}

/* Card */
.card {
  border: none;
  box-shadow: 0 2px 12px rgba(0,0,0,0.08);
}
</style>